import type { ChatLine } from '@somnhold/protocol';
import { t } from '../i18n.ts';
import { clear, h } from '../ui/dom.ts';
import { fmtStamp, fmtWhen } from '../ui/format.ts';

/** Quick lines, one tap each (a phone keyboard over the battle is slow). */
const QUICK = ['ready', 'wait', 'go', 'help', 'thanks'] as const;

/** Where the lines come from and go: a live room (its lockstep client) or the keep's chat. */
export interface ChatChannel {
  readonly chat: ChatLine[];
  onChat: (line: ChatLine) => void;
  say(text: string): void;
}

/**
 * Chat of a live room (waiting room, incursion or crypt with friends) or of the keep: open while
 * gathering; over the field a button with the unread count. Enter opens it, Enter sends and folds it
 * again; each new line shows a moment over the speaker's head (`onLine`), or floats by the button
 * when the speaker isn't on the field.
 */
export class ChatBox {
  readonly el: HTMLElement;
  private log: HTMLElement;
  private input: HTMLInputElement;
  private badge: HTMLElement;
  private float: HTMLElement;
  private open = true;
  private unread = 0;
  private floatTimer = 0;
  private whenTimer = 0;
  /** The log reads from its last line unless the player scrolled back up. */
  private atEnd = true;
  private sizer: ResizeObserver;

  constructor(
    private client: ChatChannel,
    private me: string,
    private names: Record<string, string>,
    /** A new line from someone: true when it was shown over their head. */
    private onLine: (line: ChatLine) => boolean = () => false,
  ) {
    this.log = h('div.chat-log', { role: 'log', 'aria-live': 'polite', 'data-empty': t('chat.empty') });
    // Built off the page (the lines come in before the card is up), a scroll to the end there does nothing:
    // once the log gets its size on screen it goes to the last line, unless the player had scrolled up.
    this.log.addEventListener('scroll', () => {
      if (this.log.clientHeight) this.atEnd = this.log.scrollTop + this.log.clientHeight >= this.log.scrollHeight - 8;
    });
    this.sizer = new ResizeObserver(() => this.atEnd && this.toEnd());
    this.sizer.observe(this.log);
    this.input = h('input.chat-input', { type: 'text', maxlength: 140, placeholder: t('chat.placeholder'), 'aria-label': t('chat.placeholder'), enterkeyhint: 'send' }) as HTMLInputElement;
    const send = (): void => {
      const text = this.input.value.trim();
      if (!text) return;
      this.client.say(text);
      this.input.value = '';
      // Over the field, sending folds it away again so the fight shows.
      if (!this.inCard) this.setOpen(false);
    };
    this.input.addEventListener('keydown', (e) => {
      e.stopPropagation();
      if (e.key === 'Enter') {
        e.preventDefault();
        if (this.input.value.trim()) send();
        else if (!this.inCard) this.setOpen(false);
      } else if (e.key === 'Escape' && !this.inCard) this.setOpen(false);
    });
    // The keyboard takes a moment to come up and shrink the page: then the line being typed shows.
    this.input.addEventListener('focus', () => {
      const show = (): void => {
        if (document.activeElement === this.input) this.input.scrollIntoView({ block: 'nearest' });
      };
      window.visualViewport?.addEventListener('resize', show, { once: true });
      window.setTimeout(show, 400);
    });
    // Sending keeps the keyboard up (a tap on the button would take the focus and fold it).
    const sendBtn = h('button.btn.small', { 'data-test': 'chat-send', onclick: send, onmousedown: (e: Event) => e.preventDefault() }, t('chat.send'));
    const form = h('div.chat-form', {}, this.input, sendBtn);
    const quick = h('div.chat-quick', {}, ...QUICK.map((k) => h('button.btn.small.ghost', { 'data-quick': k, onclick: () => this.client.say(t(`chat.q.${k}`)) }, t(`chat.q.${k}`))));
    this.badge = h('span.chat-badge');
    const toggle = h('button.iconbtn.chat-toggle', { 'data-test': 'chat-toggle', 'aria-label': t('chat.title'), onclick: () => this.setOpen(!this.open) }, h('span', {}, '💬'), this.badge);
    this.float = h('div.chat-float');
    this.el = h('div.chat', { 'data-test': 'chat' }, toggle, h('div.chat-panel', {}, this.log, quick, form), this.float);
    // Keys typed here are for the chat, never the battle's shortcuts.
    this.el.addEventListener('pointerdown', (e) => e.stopPropagation());
    client.onChat = (line) => this.add(line);
    for (const line of client.chat) this.add(line, true);
    this.setOpen(true);
    // "Just now" grows old while the log is open.
    this.whenTimer = window.setInterval(() => this.open && this.paintWhen(), 30_000);
  }

  /**
   * Into the waiting room's card (`null`: it stays where the card put it), always open there; or over the
   * field once the fight starts, folded into its button.
   */
  dock(into: HTMLElement | null): void {
    this.el.classList.toggle('docked', !into);
    if (into) {
      into.append(this.el);
      this.setOpen(false);
    } else this.setOpen(true);
  }

  /** In the waiting room's card (always open there), rather than over the field. */
  private get inCard(): boolean {
    return this.el.classList.contains('docked');
  }

  /** Enter over the field: open, ready to type. */
  focus(): void {
    this.setOpen(true);
    this.input.focus();
  }

  /** Open in the waiting room, folded into its button once the fight starts. */
  setOpen(open: boolean): void {
    this.open = open;
    this.el.classList.toggle('open', open);
    if (open) {
      this.unread = 0;
      this.paintBadge();
      this.paintWhen();
      this.toEnd();
    } else this.input.blur();
  }

  private add(line: ChatLine, quiet = false): void {
    // After a welcome the room sends its recent lines again: the client's list is the truth.
    if (this.client.chat.length <= 1) clear(this.log);
    const who = line.pid === this.me ? t('chat.you') : (this.names[line.pid] ?? t('chat.someone'));
    const when = h('time.chat-when', { datetime: new Date(line.at).toISOString(), title: fmtStamp(line.at) });
    this.log.append(h(`p.chat-line${line.pid === this.me ? '.mine' : ''}`, { 'data-at': line.at }, when, h('b', {}, who), ' ', line.text));
    while (this.log.children.length > 30) this.log.firstElementChild?.remove();
    this.paintWhen();
    this.toEnd();
    if (quiet) return;
    const overHead = this.onLine(line);
    if (line.pid === this.me) return;
    if (!this.open) {
      this.unread++;
      this.paintBadge();
      if (overHead) return;
      clear(this.float);
      this.float.append(h('b', {}, who), ' ', line.text);
      this.float.classList.add('show');
      window.clearTimeout(this.floatTimer);
      this.floatTimer = window.setTimeout(() => this.float.classList.remove('show'), 5000);
    }
  }

  /** A signal someone put on the board, kept in the log (only on this screen, never sent; no unread count). */
  note(pid: string, text: string, color: string): void {
    const who = pid === this.me ? t('chat.you') : (this.names[pid] ?? t('chat.someone'));
    const at = Date.now();
    const when = h('time.chat-when', { datetime: new Date(at).toISOString(), title: fmtStamp(at) });
    this.log.append(h('p.chat-line.sig-note', { 'data-at': at, style: `--c:${color}` }, when, h('b', {}, who), ' ', h('span', {}, text)));
    while (this.log.children.length > 30) this.log.firstElementChild?.remove();
    this.paintWhen();
    this.toEnd();
  }

  /**
   * How long ago each line was said; a run of lines from the same moment carries it once, on its last line:
   * the log sits scrolled to the bottom, and on its first line it hid above the fold.
   */
  private paintWhen(): void {
    const now = Date.now();
    let next = '';
    for (const p of [...this.log.children].reverse()) {
      const el = p.firstElementChild;
      if (!(el instanceof HTMLElement) || !el.classList.contains('chat-when')) continue;
      const when = fmtWhen(Number((p as HTMLElement).dataset.at), now);
      el.textContent = when === next ? '' : when;
      next = when;
    }
  }

  private toEnd(): void {
    this.log.scrollTop = this.log.scrollHeight;
    this.atEnd = true;
  }

  private paintBadge(): void {
    this.badge.textContent = this.unread ? String(this.unread) : '';
    this.badge.hidden = this.unread === 0;
  }

  dispose(): void {
    window.clearTimeout(this.floatTimer);
    window.clearInterval(this.whenTimer);
    this.sizer.disconnect();
    this.client.onChat = () => undefined;
    this.el.remove();
  }
}
